import React, { useState, useEffect, useRef } from "react";
import { ResponsiveLine } from "@nivo/line";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { zeroIfNegative } from "../../../Utils/helper";

export default function SkuGraphPanel({
  skuItem,
  onClose,
  Product,
  Location,
  bestActPredData,
  multipleActPredData,
  selectedModel,
  compact = false,
}) {
  const [plotData, setPlotData] = useState([]);
  const [activeModelIndex, setActiveModelIndex] = useState(0);
  const [skuData, setSkuData] = useState(null);
  const [graphTab, setGraphTab] = useState("actual");
  const [pdfDownloading, setPdfDownloading] = useState(false);
  // Clicking a line (or its legend entry) highlights it and fades the others;
  // clicking the same one again (or elsewhere) clears the selection.
  const [highlightedSeries, setHighlightedSeries] = useState(null);
  const pdfRef = useRef(null);
  const chartBoxRef = useRef(null);

  const skuName = skuItem?.sku_name || skuItem?.name || Product;

  useEffect(() => {
    const isBestModelMode = selectedModel === "bestModel";
    console.log("cities from best model response", bestActPredData);
    const dataSource = isBestModelMode ? bestActPredData : multipleActPredData;

    if (dataSource?.status !== "success") return;

    try {
      let cities = [];

      if (dataSource?.data?.data?.cities) {
        cities = dataSource.data.data.cities; // best model response
      } else if (dataSource?.data?.cities) {
        cities = dataSource.data.cities; // multiple model response
      }

      const targetCity = Location || cities?.[0]?.city;
      const cityData = cities?.find((city) => city.city === targetCity) || cities?.[0];
      if (!cityData) return;

      const found =
        cityData?.data?.find(
          (sku) => (sku?.sku || "").trim().toLowerCase() === (skuName || "").trim().toLowerCase()
        ) || null;

      if (!found) return;

      setSkuData(found);

      let modelIndexToUse = 0;

      if (!isBestModelMode && selectedModel && found.multiple_model_data?.length) {
        const modelIndex = found.multiple_model_data.findIndex(
          (model) => model.model_name === selectedModel
        );
        if (modelIndex >= 0) {
          modelIndexToUse = modelIndex;
          setActiveModelIndex(modelIndex);
        }
      } else {
        setActiveModelIndex(0);
      }

      drawChart(found, modelIndexToUse, isBestModelMode);
    } catch (err) {
      console.error("Error processing graph data:", err);
    }
  }, [multipleActPredData, bestActPredData, Location, skuName, selectedModel]);
  useEffect(() => {
    if (skuData) {
      const isBestModelMode = selectedModel === "bestModel";
      drawChart(skuData, activeModelIndex, isBestModelMode);
    }
  }, [activeModelIndex, skuData, selectedModel]);

  function drawChart(data, modelIdx, isBestModelMode = false) {
    let modelData = [];

    if (isBestModelMode) {
      modelData = data?.data || [];
    } else {
      modelData = data?.multiple_model_data?.[modelIdx]?.data || [];
    }

    if (!modelData.length) {
      setPlotData([]);
      return;
    }

    // 🔥 Apply zeroIfNegative to ALL y values before chart rendering
    const actualSeries = {
      id: "Actual",
      color: "#032B4E",
      data: modelData.map((item) => ({
        x: item.frequency_year,
        y: item.actual,
      })),
    };

    const predictedData = modelData.map((item) => ({
      x: item.frequency_year,
      y: zeroIfNegative(item.predicted),
    }));

    // 🔥 FIXED: Show all historical data as "Predicted" (green) and all future as "Forecast" (orange)
    // Find the cutoff where forecast starts (typically where actual data ends)
    const actualEndIndex = actualSeries.data.findLastIndex(point => point.y > 0);
    const forecastStartIndex = Math.max(actualEndIndex + 1, predictedData.length - 6); // Last 6 periods as forecast

    const predictedNormal = {
      id: "Predicted",
      color: "#2EB8AA",
      data: predictedData.slice(0, forecastStartIndex),
    };

    const predictedHighlighted = {
      id: "Forecast",
      color: "#F2994A",
      data: predictedData.slice(forecastStartIndex - 1), // Overlap 1 point for smooth transition
    };

    console.log("actualSeries", actualSeries);
    console.log("predictedNormal", predictedNormal);
    console.log("predictedHighlighted", predictedHighlighted);

    setPlotData([actualSeries, predictedNormal, predictedHighlighted]);
    setHighlightedSeries(null);
  }

  const modelName =
    selectedModel === "bestModel"
      ? skuData?.top_model_name || "Best Model"
      : skuData?.multiple_model_data?.[activeModelIndex]?.model_name || "Model";

  // Slice tooltip: shows every series (Actual, Predicted, Forecast) at the
  // hovered x-position together, instead of nivo's default of only the one
  // point directly under the cursor. Nivo's tooltip is positioned using raw
  // cursor pixel coordinates with no awareness of the modal/container edges,
  // so it can render past the right side of the chart — flip it left when
  // the hovered slice is close to the container's right edge.
  const renderSliceTooltip = ({ slice }) => {
    const containerWidth = chartBoxRef.current?.clientWidth || 0;
    const shouldFlip = containerWidth > 0 && slice.x > containerWidth - 170;
    const points = slice.points.filter((p) => p.data.y !== null && p.data.y !== undefined);
    if (!points.length) return null;

    return (
      <div
        style={{
          background: "#fff",
          padding: "8px 12px",
          borderRadius: 8,
          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          fontSize: 12,
          color: "#222",
          whiteSpace: "nowrap",
          transform: shouldFlip ? "translateX(-100%)" : "none",
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 6 }}>{points[0].data.xFormatted}</div>
        {points.map((point) => (
          <div
            key={point.serieId}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 0" }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: point.serieColor,
                display: "inline-block",
                flexShrink: 0,
              }}
            />
            <span>
              {point.serieId}: <strong>{point.data.yFormatted}</strong>
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Fade every line except the one selected via click (on a point or its
  // legend entry). `colors={{ datum: "color" }}` reads this per-series
  // `.color` field, so overriding it here is enough — no other prop needed.
  const displayData = plotData.map((serie) =>
    !highlightedSeries || serie.id === highlightedSeries
      ? serie
      : { ...serie, color: serie.color + "40" }
  );

  const toggleHighlight = (id) =>
    setHighlightedSeries((prev) => (prev === id ? null : id));

  // Nivo's built-in `onClick` only fires on point markers via a shared
  // hit-test mesh, which is unreliable here since Actual and Predicted are
  // nearly identical for historical periods — their points sit almost on
  // top of each other, so a click can resolve to the wrong series. This
  // custom layer draws an invisible, thick, clickable path directly over
  // each line (the same "click anywhere on this line" behavior the trend
  // analysis chart gets from recharts attaching onClick per SVG path) and
  // is rendered above nivo's `mesh` layer so it actually receives the click.
  const ClickableLines = ({ series, lineGenerator }) => (
    <g>
      {series.map((serie) => (
        <path
          key={serie.id}
          d={lineGenerator(serie.data.map((d) => d.position))}
          fill="none"
          stroke="transparent"
          strokeWidth={16}
          style={{ cursor: "pointer", pointerEvents: "stroke" }}
          onClick={() => toggleHighlight(serie.id)}
        />
      ))}
    </g>
  );

  const downloadPdf = async () => {
    if (!pdfRef.current) return;
    setPdfDownloading(true);
    try {
      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${skuName || "SKU"}_${modelName || "Model"}_Report.pdf`);
    } catch (err) {
      console.error("PDF download failed:", err);
    } finally {
      setPdfDownloading(false);
    }
  };

  return (
    <div ref={pdfRef} style={{ display: "flex", flexDirection: "column", height: compact ? "auto" : "130%", background: "#f8fafc" }}>
      {/* Chart Container */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          padding: "0 8px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#032B4E",
              lineHeight: 1.2,
            }}
          >
            {skuName}
          </div>

          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#475569",
              marginTop: 4,
            }}
          >
            Model: {modelName}
          </div>
        </div>
      </div>

     <div
  ref={chartBoxRef}
  className="rounded-lg p-4"
  style={{
    border: "2px solid #032B4E",
    height: compact ? "clamp(360px, 60vh, 520px)" : "1000px",
    width: compact ? "calc(100% - 32px)" : "95%",
    maxWidth: "100%",
    boxSizing: "border-box",
    backgroundColor: "#f8fdf9",
    margin: compact ? "0 16px 16px" : "24px",
    borderRadius: 20,
    overflow: compact ? "hidden" : "visible",
    position: "relative",
    display: "flex",
    flexDirection: "column",
  }}
>
      <div style={{ flex: 1, minHeight: 0 }}>
        {plotData.length > 0 ? (
          <ResponsiveLine
            data={displayData}
            enableSlices="x"
            sliceTooltip={renderSliceTooltip}
            onClick={(point) => toggleHighlight(point.serieId)}
            layers={["grid", "markers", "axes", "areas", "crosshair", "lines", "points", "slices", "mesh", ClickableLines]}
            margin={
              compact
                ? { top: 30, right: 30, bottom: 60, left: 60 }
                : { top: 50, right: 40, bottom: 70, left: 70 }
            }
            xScale={{ type: "point" }}
            yScale={{ type: "linear", min: "auto", max: "auto", stacked: false }}
            colors={{ datum: "color" }}
            axisBottom={{
              orient: "bottom",
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
            }}
            useMesh={true}
            pointSize={compact ? 6 : 8}
          />
        ) : (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#94a3b8",
              fontSize: 16,
            }}
          >
            No chart data available for this SKU
          </div>
        )}
      </div>

        {/* Custom HTML Legend */}
        {plotData.length > 0 && (
          <div style={{ display: "flex", justifyContent: "center", gap: compact ? 30 : 60, marginTop: 4, marginBottom: 8 }}>
            {plotData.map((serie) => {
              const isSelected = highlightedSeries === serie.id;
              return (
                <div
                  key={serie.id}
                  onClick={() => toggleHighlight(serie.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      width: compact ? 12 : 18,
                      height: compact ? 12 : 18,
                      borderRadius: "50%",
                      background: serie.color,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: compact ? 13 : 17,
                      fontWeight: isSelected ? 800 : 600,
                      color: isSelected ? "#032B4E" : "#374151",
                      textDecoration: isSelected ? "underline" : "none",
                    }}
                  >
                    {serie.id}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}